<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateRental',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const defaultCompany = ref(null);
  const resource = ref(null);
  const editor = ref(null);
  const container = ref(null);
  const formName = ref('rental');
  const formId = ref(null);
  const resourceTitle = ref($fn.value.pgettext('Rental', 'Rental'));
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    defaultCompany: defaultCompany.value,
    resource: resource.value,
    editor: editor.value,
    container: container.value,
    formName: formName.value,
    formId: formId.value,
    resourceTitle: resourceTitle.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_defaultCompany = () => {
    return $fn.value.fetch('read_company', { parameters: { id: 'default' } });
  };
  const $$load_resource = () => {
    return $fn.value.schemaDefaults('CreateRental', {
      company: defaultCompany.value,
      valid_from: $fn.value.today(),
    });
  };
  const $$load_editor = () => {
    return resource.value.editor();
  };
  const $$load_container = () => {
    return {
      self: $fn.value.urlFor('create_rental'),
      object: 'list',
      data: [],
    };
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'defaultCompany' },
      { name: 'resource', dependsOn: ['defaultCompany'] },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'container' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'resourceTitle' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      defaultCompany: $$load_defaultCompany,
      resource: $$load_resource,
      editor: $$load_editor,
      container: $$load_container,
      formId: $$load_formId,
    },
    ux: {},
    dataRefs: {
      defaultCompany,
      resource,
      editor,
      container,
      formName,
      formId,
      resourceTitle,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Rental', 'Create Rental') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_tcklpas5m_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_bu7fya96l_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_navigate_5wnuoxen9_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.rental',
        params: {
          id: $event.data.response.data.id,
        },
      },
    };
  };
  const act_1040167445267876_reloadGrid_thl2e5lnj_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'rentals',
    };
  };
  const act_1040167445267876_rest_kbdr2t152_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_tcklpas5m =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_tcklpas5m_getActionArgs,
    });
  const inst_evh_7315092382398562_close_29j2lomr1 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_tcklpas5m,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_29j2lomr1],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_c3yuoryiy =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_m5xmu3hb6 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_c3yuoryiy,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_m5xmu3hb6],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_y8j931rcr =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_vrv594cgl =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_y8j931rcr,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_vrv594cgl],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_bu7fya96l =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_bu7fya96l_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_038njkbt2 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_bu7fya96l,
      event: 'saved',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_navigate_5wnuoxen9 =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_5wnuoxen9_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_b00h31kjn =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_5wnuoxen9,
      event: 'saved',
      displayName: 'navigate',
    });
  const act_1040167445267876_reloadGrid_thl2e5lnj =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_thl2e5lnj_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_rjqysrew1 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_thl2e5lnj,
      event: 'saved',
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_rest_kbdr2t152 = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_kbdr2t152_getActionArgs,
    displayName: 'create',
    events: [
      inst_evh_1040167445267876_saved_038njkbt2,
      inst_evh_1040167445267876_saved_b00h31kjn,
      inst_evh_1040167445267876_saved_rjqysrew1,
    ],
  });
  const inst_evh_1040167445267876_submit_ldo95u7gr =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_kbdr2t152,
      event: 'submit',
      displayName: 'create',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_ldo95u7gr],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_kkq6ckhvh =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_ai4m6uj73 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_kkq6ckhvh,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_ai4m6uj73],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_29j2lomr1,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_m5xmu3hb6,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_vrv594cgl,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_saved_038njkbt2,
    inst_evh_1040167445267876_saved_b00h31kjn,
    inst_evh_1040167445267876_saved_rjqysrew1,
    inst_evh_1040167445267876_submit_ldo95u7gr,
    create: inst_evh_1040167445267876_submit_ldo95u7gr,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_ai4m6uj73,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_29j2lomr1 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_29j2lomr1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_m5xmu3hb6 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_m5xmu3hb6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_vrv594cgl = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_vrv594cgl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_038njkbt2 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_038njkbt2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_b00h31kjn = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_b00h31kjn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_rjqysrew1 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_rjqysrew1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_ldo95u7gr = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_ldo95u7gr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_ai4m6uj73 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_ai4m6uj73.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateRental"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :dialogName="resourceTitle"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateRental"
        :id="formId"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetRentalGeneralData :rental="editor">
        </FieldsetRentalGeneralData>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          type="submit"
          :form="forms[formName]"
          :spinning="actions.create.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.create.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
